import React, { useRef, useEffect } from 'react';
import { Box, Button, ButtonGroup } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';

export default function RichTextEditor({ value, onChange, placeholder = 'Enter content...' }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  return (
    <>
      <style>{`
        .rich-text-editor[contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #999;
          pointer-events: none;
        }
      `}</style>
      <Box sx={{ border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden' }}>
        {/* Toolbar */}
      <Box
        sx={{
          borderBottom: '1px solid #ccc',
          p: 0.5,
          bgcolor: '#f5f5f5',
          display: 'flex',
          gap: 0.5,
          flexWrap: 'wrap',
        }}
      >
        <ButtonGroup size="small" variant="outlined">
          <Button
            onClick={() => execCommand('bold')}
            sx={{ minWidth: 40, p: 0.5 }}
            title="Bold"
          >
            <FormatBoldIcon fontSize="small" />
          </Button>
          <Button
            onClick={() => execCommand('italic')}
            sx={{ minWidth: 40, p: 0.5 }}
            title="Italic"
          >
            <FormatItalicIcon fontSize="small" />
          </Button>
          <Button
            onClick={() => execCommand('underline')}
            sx={{ minWidth: 40, p: 0.5 }}
            title="Underline"
          >
            <FormatUnderlinedIcon fontSize="small" />
          </Button>
        </ButtonGroup>
        <ButtonGroup size="small" variant="outlined">
          <Button
            onClick={() => execCommand('insertUnorderedList')}
            sx={{ minWidth: 40, p: 0.5 }}
            title="Bullet List"
          >
            <FormatListBulletedIcon fontSize="small" />
          </Button>
          <Button
            onClick={() => execCommand('insertOrderedList')}
            sx={{ minWidth: 40, p: 0.5 }}
            title="Numbered List"
          >
            <FormatListNumberedIcon fontSize="small" />
          </Button>
        </ButtonGroup>
        <ButtonGroup size="small" variant="outlined">
          <Button
            onClick={() => execCommand('formatBlock', '<h2>')}
            sx={{ minWidth: 40, p: 0.5, fontSize: '0.75rem' }}
            title="Heading"
          >
            H2
          </Button>
          <Button
            onClick={() => execCommand('formatBlock', '<h3>')}
            sx={{ minWidth: 40, p: 0.5, fontSize: '0.75rem' }}
            title="Subheading"
          >
            H3
          </Button>
        </ButtonGroup>
      </Box>

      {/* Editor */}
      <Box
        component="div"
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        className="rich-text-editor"
        sx={{
          minHeight: '300px',
          p: 2,
          outline: 'none',
          bgcolor: 'white',
          '&:focus': {
            outline: 'none',
          },
          '& p': {
            margin: '0.5rem 0',
          },
          '& ul, & ol': {
            marginLeft: '1.5rem',
            marginTop: '0.5rem',
            marginBottom: '0.5rem',
          },
          '& h2, & h3': {
            marginTop: '1rem',
            marginBottom: '0.5rem',
          },
        }}
      />
      </Box>
    </>
  );
}

